import React from "react";
import {
  Box,
  VStack,
  Text,
  Image,
  Container,
  Heading,
  useColorModeValue,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Helmet } from "react-helmet-async";

const NotFoundPage = () => {
  const textColor = useColorModeValue("gray.600", "gray.300");
  const headingColor = useColorModeValue("#000B8C", "blue.300");

  return (
    <>
      <Helmet>
        <title>Page Not Found - Business Automation Ltd</title>
        <meta
          name="description"
          content="The page you're looking for is no longer active. Visit our new website for the latest content."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Box
        sx={{
          backgroundImage: "url('/png/background.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <Box pb={{ base: 4, md: 6, lg: 20 }} pt={{ base: 2, md: 4, lg: 6 }}>
          <Container maxW="4xl" centerContent>
            <VStack 
              textAlign="center" 
              maxW={{ base: "full", sm: "xl", md: "2xl" }} 
              px={{ base: 3, sm: 6, md: 8 }}
            //   spacing={{ base: 1, md: 6, lg: 8 }}
              w="100%"
            >
              {/* Illustration */}
              <Box position="relative" mb={{ base: 0, md: 4 }}>
                <Image
                  src="/png/page_not_active.png"
                  alt="Page not found illustration"
                  maxW={{ base: "220px", sm: "280px", md: "350px", lg: "400px" }}
                  w="90%"
                  h="auto"
                  mx="auto"
                  fallback={
                    <Box
                      w={{ base: "250px", sm: "300px", md: "350px", lg: "400px" }}
                      h={{ base: "180px", sm: "220px", md: "262px", lg: "300px" }}
                      bg="blue.100"
                      borderRadius="lg"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      color="blue.600"
                      fontSize={{ base: "2xl", sm: "3xl", md: "4xl" }}
                      fontWeight="bold"
                    >
                      404
                    </Box>
                  }
                />
              </Box>

              {/* Current URL display */}
              <Box
                fontSize={{ base: "2xs", sm: "xs", md: "sm" }}
                pt={8}
                color="gray.500"
                fontFamily="mono"
                wordBreak="break-all"
                lineHeight="1.3"
                maxW="100%"
                px={{ base: 1, sm: 0 }}
                mb={{ base: 0, md: 1 }}
              >
                {window.location.href}
              </Box>

              {/* Main heading */}
              <Heading
                as="h1"
                fontSize={{ base: "18px", sm: "22px", md: "32px", lg: "36px" }}
                color={headingColor}
                fontWeight="600"
                lineHeight={{ base: "1.2", md: "1.2" }}
                // mb={{ base: 1, md: 4 }}
                px={{ base: 1, sm: 0 }}
                mb={{ base: 0, md: 2 }}
                mt={{ base: -1, md: 0 }}
              >
                This link is no longer active.
              </Heading>

              {/* Description */}
              <VStack
                spacing={{ base: 1, md: 4 }}
                color={textColor}
                fontSize={{ base: "13px", sm: "14px", md: "16px" }}
                lineHeight={{ base: "1.4", md: "1.6" }}
                maxW={{ base: "100%", sm: "90%", md: "100%" }}
                pb={{ base: 2, md: 0 }}
              >
                <Text px={{ base: 1, sm: 0 }}>
                  We've recently revamped our website to give you a better
                  browsing experience. Please visit our new home at{" "}
                  <ChakraLink
                    href="https://ba-systems.com/"
                    color="#000B8C"
                    fontWeight="semibold"
                    textDecoration="underline"
                    _hover={{ color: "blue.600" }}
                    display={{ base: "inline-block", sm: "inline" }}
                    wordBreak={{ base: "break-all", sm: "normal" }}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    https://ba-systems.com/
                  </ChakraLink>
                </Text>

                <Text px={{ base: 1, sm: 0 }}>
                  If you're looking for something specific or need assistance,
                  reach us anytime via our Contact Us{" "}
                  <ChakraLink
                    href="https://ba-systems.com/contact"
                    color="#000B8C"
                    fontWeight="semibold"
                    textDecoration="underline"
                    _hover={{ color: "blue.600" }}
                    target="_blank"
                    rel="noopener noreferrer"
                    display={{ base: "inline-block", sm: "inline" }}
                    wordBreak={{ base: "break-all", sm: "normal" }}
                  >
                    https://ba-systems.com/contact
                  </ChakraLink>{" "}
                  Page
                </Text>
              </VStack>
            </VStack>
          </Container>
        </Box>
      </Box>
    </>
  );
};

export default NotFoundPage;
